import type { IFieldOption, IInsightSettings, IRecordTypeDefinition, IViewSettings } from '@platform/core/types';
import { narrowInsightSettings } from './narrowInsightSettings';

/** Налаштування подання з views.settings типу запису (table, backlog, calendar, … та власні), якщо пакет чи сервер їх передали. */
export function viewSettingsOf(rt: IRecordTypeDefinition, view: string): IViewSettings | undefined {
  return rt.views.settings?.[view];
}

/** Колонки списку без налаштувань подання: views.columns або всі поля inList (title — завжди окрема колонка). */
export function defaultColumns(rt: IRecordTypeDefinition): string[] {
  return rt.views.columns ?? rt.fields.filter((f) => f.inList !== false && f.key !== 'title').map((f) => f.key);
}

/** Колонки подання (table, split, …): settings.<подання>.columns, інакше fallback. */
export function columnsFor(rt: IRecordTypeDefinition, view: string, fallback: string[]): string[] {
  return nonEmpty(viewSettingsOf(rt, view)?.columns) ?? fallback;
}

/** Поля подання (cards, inline, …): settings.<подання>.fields, інакше fallback. */
export function fieldsFor(rt: IRecordTypeDefinition, view: string, fallback: string[]): string[] {
  return nonEmpty(viewSettingsOf(rt, view)?.fields) ?? fallback;
}

/** Поле колонок дошки: settings.backlog.groupBy → views.boardGroupBy → status. */
export function groupByFor(rt: IRecordTypeDefinition): string {
  return viewSettingsOf(rt, 'backlog')?.groupBy || rt.views.boardGroupBy || 'status';
}

/** Поле вкладок подання «Етапи»: settings.stages.by (перше, якщо задано список) → status. */
export function stagesByFor(rt: IRecordTypeDefinition): string {
  return byList(viewSettingsOf(rt, 'stages')?.by)[0] ?? 'status';
}

/** Поля панелі показників — один блок статистики на кожне, перше — головне: settings.dashboard.by → ['status']. */
export function dashboardByFor(rt: IRecordTypeDefinition): string[] {
  const list = byList(viewSettingsOf(rt, 'dashboard')?.by);
  return list.length ? list : ['status'];
}

/** Поля дат календаря у порядку пріоритету: settings.calendar.dateFields → due → перше поле типу date (порожньо — календар недоступний). */
export function dateFieldsFor(rt: IRecordTypeDefinition): string[] {
  const configured = nonEmpty(viewSettingsOf(rt, 'calendar')?.dateFields);
  if (configured) return configured;
  const fallback = rt.fields.some((f) => f.key === 'due') ? 'due' : rt.fields.find((f) => f.type === 'date')?.key;
  return fallback ? [fallback] : [];
}

/** Значення поля групування з підписами визначення: статуси типу запису для `status`, інакше варіанти select-поля. */
export function groupValues(rt: IRecordTypeDefinition, by: string): IFieldOption[] {
  if (by === 'status') return rt.statuses.list.map((s) => ({ value: s.value, label: s.label, color: s.color }));
  return rt.fields.find((f) => f.key === by)?.options ?? [];
}

const nonEmpty = (list: string[] | undefined): string[] | undefined => (list && list.length ? list : undefined);
const byList = (by: string | string[] | undefined): string[] => (Array.isArray(by) ? by : by ? [by] : []).filter(Boolean);

const NO_INSIGHT: IInsightSettings = Object.freeze({});
/** Звужені параметри за вихідним об'єктом визначення: той самий insight — той самий результат (useMemo компонентів не перераховуються). */
const narrowed = new WeakMap<object, IInsightSettings>();

/**
 * Параметри аналітичного подання (views.settings.<ключ>.insight), звужені narrowInsightSettings (невідомі kind, mode і тони відкидаються),
 * або порожній об'єкт, якщо пакет/сервер їх не задав (чи задав не об'єктом). Звужуються обидва джерела: файли пакета (assembleViewSet)
 * і визначення з сервера, яке assembleViewSet не проходить.
 */
export function insightSettingsOf(rt: IRecordTypeDefinition, view: string): IInsightSettings {
  const raw = viewSettingsOf(rt, view)?.insight;
  // Сервер віддає views.settings як сирий JSON: не об'єкт (рядок, масив) — не параметри, а ключем WeakMap може бути лише об'єкт.
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return NO_INSIGHT;
  let settings = narrowed.get(raw);
  if (!settings) {
    settings = narrowInsightSettings(raw);
    narrowed.set(raw, settings);
  }
  return settings;
}
